import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { CasesRepository } from '../../../infrastructure/repositories/cases.repository';
import { CasesState } from '../../states/cases.state';
import type { GetCaseFieldValuesQuery } from './get-case-field-values.query';
import type { GetCaseFieldValuesResult } from './get-case-field-values.result';

/**
 * Get case field values use case.
 * Fetches case with populated field values from the dedicated field-values endpoint.
 * Updates selectedCase in state with populated field values.
 */
@Injectable({ providedIn: 'root' })
export class GetCaseFieldValuesUseCase {
  private readonly _repository = inject(CasesRepository);
  private readonly _state = inject(CasesState);

  public execute(query: GetCaseFieldValuesQuery): Observable<GetCaseFieldValuesResult> {
    return this._repository.getCaseFieldValues(query.caseId).pipe(
      map((fieldValues) => {
        // Get the case entity from state (already loaded)
        const caseEntity = this._state.selectedCase();
        if (!caseEntity) {
          throw new Error('Case entity not found in state');
        }

        // Update case entity with field values
        const updatedCase = { ...caseEntity, fieldValues };
        this._state.setSelectedCase(updatedCase);

        return {
          fieldValues: fieldValues,
          case: updatedCase,
        };
      }),
      catchError((error) => {
        const errorMessage = error.message || 'Failed to load case field values';
        this._state.setErrorCases(errorMessage);
        this._state.setLoadingCases(false);
        return throwError(() => error);
      })
    );
  }
}
